import { AlertCircle, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface PageErrorStateProps {
  /** One-line headline. Defaults to a generic load-failure message. */
  title?: string;
  /** Optional supporting detail (e.g. the server error message). */
  description?: string;
  /** Retry handler. When provided, renders a Retry button. */
  onRetry?: () => void;
  /** Retry button label. */
  retryLabel?: string;
  /** Disables the retry button (e.g. while a refetch is in flight). */
  retrying?: boolean;
  className?: string;
}

/**
 * Full-surface page-level error block (DESIGN.md §15.3). Use when a query fails
 * entirely and rendering a partial page would mislead. Destructive accents on
 * the icon + title; calm neutral body copy; a single Retry affordance.
 */
export function PageErrorState({
  title = "Something went wrong",
  description,
  onRetry,
  retryLabel = "Retry",
  retrying = false,
  className,
}: PageErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center py-12 text-center",
        className,
      )}
    >
      <AlertCircle className="h-12 w-12 text-destructive mb-4" aria-hidden="true" />
      <p className="text-lg font-medium text-destructive">{title}</p>
      {description ? (
        <p className="text-muted-foreground text-sm max-w-sm mt-1">
          {description}
        </p>
      ) : null}
      {onRetry ? (
        <Button
          variant="outline"
          className="mt-4 gap-1.5"
          onClick={onRetry}
          disabled={retrying}
        >
          <RefreshCw
            className={cn("h-3.5 w-3.5", retrying && "animate-spin")}
            aria-hidden="true"
          />
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
